/**
 * The embed contract's boolean grammar (D-211).
 *
 * POST's embed generators are Python, and they serialise a boolean with
 * `str(True)` — so the wire carries `True`, not `true`. `classes/widget.js` has
 * always accepted BOTH spellings, in its `vertical` branch and its `full`
 * branch. Live traffic carries both: `full=True` runs 385 loads/day from POST's
 * listen pages, and `vertical=True` 46/day.
 *
 * ✅ ALL THREE widget-side readers route through this helper, so the grammar is
 * single-sourced across the iframe boundary: `parseCoreConfig`'s `full` and
 * `verticalEmbed` (D-211), and `parseTheme`'s `vertical` (D-215). `verticalEmbed`
 * is the one an earlier revision of this list left out — it is the gate's WIRE
 * discriminator, deliberately separate from `parseTheme`'s layout flag even though
 * both compute the same value.
 * 🪤 THERE WERE FOUR, AND `useM4a` WAS THE FOURTH — do not restore it from an older
 * revision of this comment. D-215 routed it through here for grammar consistency
 * while noting it was dead downstream; D-220 then deleted the parse outright
 * (2026-09-06), so every reader named above is now load-bearing.
 *
 * 🔑 THE ACCEPTED SET IS EXACTLY `'True' | 'true'`, and must stay that way.
 * It mirrors `getNewIframeElement`'s own test character for character. `'1'`,
 * `'TRUE'`, `'yes'` and a bare valueless key are all deliberately NOT accepted,
 * because the LOADER does not accept them either — and a spelling the widget
 * honoured while the loader ignored it would size the iframe for one rendering
 * and paint the other. That divergence across the iframe boundary is the exact
 * shape of D-211, the defect this helper exists to close; widening the grammar
 * on one side only would reopen it.
 *
 * 🪤 THE CONVERSION OF `vertical` LANDED SEPARATELY, ON PURPOSE, and the reason is
 * worth keeping. This header used to say it was not converted, because routing it
 * through here CHANGES WHAT LIVE EMBEDS RENDER — 46 loads/day for `vertical=True` —
 * and that is a behavior change which deserved its own MR and its own register row
 * rather than a silent ride-along on the D-211 customer fix. D-215 is that row;
 * this note records that the split was a decision, so nobody re-reads the staged
 * rollout as an oversight.
 * `use_m4a` rode along with it but did NOT share that justification — it rendered
 * nothing either way (dead downstream). Grouping the two under one "changes what live
 * embeds render" claim is what an earlier revision of this stanza did, and it was
 * wrong for half the pair. D-220 has since deleted that parse, which is why the list
 * above says three: the asymmetry was resolved by removing the half that had no
 * consequence, not by widening the claim to cover it.
 *
 * 🔴 One live consequence of the D-215 half, since it is invisible from here:
 * contract register #3 is a LOADER bug where a sizing param in FIRST config
 * position is dropped by `handleIframe`'s `new URLSearchParams(url)` re-parse.
 * While `vertical` was lowercase-only, a first-position `vertical=True` was
 * dropped by the loader AND ignored by the widget, so both sides agreed on
 * horizontal by accident. Now the widget honours it and the loader still does
 * not, so that one position renders a vertical widget in a 700-wide iframe.
 * Register #3 is filed and pinned (see `test/e2e/contract.spec.js`) with its
 * fix-vs-preserve decision deferred to the Vue3 embedder port; D-215 does not
 * touch it. It is a pre-existing loader defect whose SHAPE this fix changes, not
 * a regression this fix introduces.
 *
 * @param {*} value a raw query-param value (a string when present, else undefined)
 * @returns {boolean} true only for the two accepted spellings
 */
export const pyBool = (value) => value === 'True' || value === 'true';

export default pyBool;
